import { SupportPolicy, SupportPolicyToUserGroup } from '@provision/entity-types';
import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import {
  DELETE_SUPPORT_POLICY_MUTATION,
  DELETE_SUPPORT_POLICY_TO_USER_GROUPS_MUTATION,
} from 'src/graphql/entities/support-policy/support-policy.operations';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';
import { GetSupportPolicyQuery } from './use-support-policy';

export interface DeleteSupportPolicyMutation {
  deleteSupportPolicy: (Pick<SupportPolicy, 'id'> & { __typename: string })[];
}

export type DeleteSupportPolicyMutationParams = {
  conditions: ConditionInput[];
};

export interface DeleteSupportPolicyToUserGroupMutation {
  deleteSupportPolicyToUserGroup: (Pick<SupportPolicyToUserGroup, 'id'> & {
    __typename: string;
  })[];
}

export type DeleteSupportPolicyToUserGroupMutationParams = {
  conditions: ConditionInput[];
};

export const useDeleteSupportPolicy = () => {
  const { showErrorAlert } = useNotifications();
  const [deleteSupportPolicyMutation, { loading: isDeleting }] = useMutation<
    DeleteSupportPolicyMutation,
    DeleteSupportPolicyMutationParams
  >(DELETE_SUPPORT_POLICY_MUTATION, {
    update(cache, { data }) {
      if (!data?.deleteSupportPolicy?.length) {
        return;
      }

      cache.modify({
        fields: {
          getSupportPolicy(cachedRef = {}) {
            const cachedData = cachedRef as GetSupportPolicyQuery['getSupportPolicy'];
            return {
              ...cachedData,
              records: cachedData.records?.filter(
                (file) => file.id !== data.deleteSupportPolicy[0].id,
              ),
              metadata: {
                ...cachedData.metadata,
                totalRecords: (cachedData.metadata?.totalRecords ?? 0) - 1,
              },
            };
          },
        },
      });
      data.deleteSupportPolicy.forEach((policy) => {
        const id = cache.identify({
          __typename: 'SupportPolicy',
          id: policy.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to delete policy, please try again');
    },
  });

  const [deleteSupportPolicyToUserGroupMutation] = useMutation<
    DeleteSupportPolicyToUserGroupMutation,
    DeleteSupportPolicyToUserGroupMutationParams
  >(DELETE_SUPPORT_POLICY_TO_USER_GROUPS_MUTATION, {
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to delete policy associations, please try again');
    },
  });

  const deleteSupportPolicy = useCallback(
    async (id: string) => {
      if (!id) {
        return;
      }

      await deleteSupportPolicyToUserGroupMutation({
        variables: {
          conditions: [
            {
              attribute: 'supportPolicyId',
              operator: '=',
              value: id,
            },
          ],
        },
      });

      await deleteSupportPolicyMutation({
        variables: {
          conditions: [
            {
              attribute: 'supportPolicyId',
              operator: '=',
              value: id,
            },
          ],
        },
        optimisticResponse: {
          deleteSupportPolicy: [
            {
              __typename: 'SupportPolicy',
              id,
            },
          ],
        },
      });
    },
    [deleteSupportPolicyMutation, deleteSupportPolicyToUserGroupMutation],
  );

  return {
    deleteSupportPolicy,
    isDeleting,
  };
};
